import { LoggingService, UserVisibleMessage } from "../sdServices/loggingSvc";
import { FlightingServiceImplementation } from "../sdServices/flightingSvc";
import { DialogService } from "../sdServices/dialog";
import { darkTheme, DisplayThemeService } from "../sdServices/display-theme-service";
import { LocalizationService } from "../../ShellCommon/localization/localization";
import { LogoutService } from "../sdServices/authentication/logout-service";
import { InformativeAlertService } from "../sdDirectives/informative-alert-service";
import { ActionRequiredAlertService } from "../sdDirectives/action-required-alert-service";
import { ServiceDeskConfig, CaptureTelemetryStorageKey } from "./serviceDeskConfig";
import { unauthorizedRequestBroadcast } from "./sd-app.adal-401-suppressor";
import { MsalAuthenticationServiceImplementation } from "../sdServices/msal/msal-authentication-service";

declare var awa: any;

export type RootScopeWithUserInfo = ng.IRootScopeService & {
  userInfo: any;
};
export type ScopeWithUserInfo = ng.IScope & {
  userInfo: any;
};
export type ScopeSdController = ScopeWithUserInfo & {
  notifyList: any[];
};
type MwfDialog = {
  show(): void;
  hide(): void;
};
export class SdController {
  public currentRootState: string;
  private localizationReady = false;
  private flightingSvcReady = false;
  private showInformativeAlert = false;
  public loadingInProgress = true;

  // @ngInject
  constructor(
    private $scope: ScopeSdController,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    $timeout: ng.ITimeoutService,
    private $state: angular.ui.IStateService,
    private $window: ng.IWindowService,
    private loggingService: LoggingService,
    private flightingService: FlightingServiceImplementation,
    private $rootScope: ng.IRootScopeService,
    private dialogService: DialogService,
    private sdLocalizationService: LocalizationService,
    private displayThemeService: DisplayThemeService,
    private logoutService: LogoutService,
    private sdConfig: ServiceDeskConfig,
    public informativeAlertService: InformativeAlertService,
    public actionRequiredAlertService: ActionRequiredAlertService
  ) {
    awa.consoleVerbosity = this.sdConfig.asimovVerbosityLevel;
    $scope.$on("msal:loginSuccess", () => {
      this.logStateInfo("msal:loginSuccess");
      this.loggingService.logInfo({ title: "Service Desk Sign In", userInfo: this.msalAuthenticationService.userInfo });
    });
    $scope.$on("msal:loginFailure", (event: ng.IAngularEvent, errorPart1: string, errorPart2: string) => {
      this.logStateInfo("msal:loginFailure");
      if ($window.location.href.indexOf("#/reauth") != -1) {
        // Don't pop the logout dialog if this is the reauthorization flow
        return;
      }

      this.loggingService.logError(
        `Silent login failure ${errorPart1} ${errorPart2}`,
        undefined,
        this.getLoginFailureMessage()
      );
    });
    $scope.$on(unauthorizedRequestBroadcast, (event: ng.IAngularEvent, url: string, resource: string) => {
      this.logStateInfo("msal:notAuthorized");
      this.loggingService.logError(
        `Not Authorized response for ${url} resource: ${resource}`,
        undefined,
        this.getLoginFailureMessage()
      );
    });
    $rootScope.$on("$stateChangeSuccess", (event, toState, toParams, fromState, fromParams) => {
      this.loggingService.logInfo(`${event.name} for ${toState.name}`);
      this.loadingInProgress = false;
      this.setRouteStates(toState);
    });
    $rootScope.$on("$stateChangeError", (event, toState, toParams, fromState, fromParams, error) => {
      this.loggingService.logError(
        `${event.name} in route to ${toState.name}: ${angular.isString(error) ? error : JSON.stringify(error)}`
      );
      let fromParamsToSend = fromParams;
      if (fromState == "sdSystem.error" && fromParams.errorParams) {
        fromParamsToSend = JSON.parse(fromParams.errorParams).fromParams;
      }
      // Don't get in a loop trying to go to the error page
      if (toState.name !== "sdSystem.error") {
        this.$state.go("sdSystem.error", {
          errorParams: {
            toState: toState.name,
            toParams: toParams,
            fromState: fromState.name,
            fromParams: fromParamsToSend,
            error: error.stack || error
          }
        });
      }
    });
    sdLocalizationService.whenReady("ShellCommon").then(() => {
      this.localizationReady = true;
    });
    if (sessionStorage.getItem(CaptureTelemetryStorageKey) === "true") {
      let anyAwa: any = awa;
      anyAwa._sde2e_originalOnSuccessListener =
        anyAwa._sde2e_originalOnSuccessListener || anyAwa.OnSuccessfulVortexRequest;
      anyAwa._sde2e_telemetryTracker = [];
      anyAwa._sde2e_failedTelemetryTracker = [];
      anyAwa.OnSuccessfulVortexRequest = function (src: any, event: {} | {}[]) {
        anyAwa._sde2e_telemetryTracker.push(event);
        anyAwa._sde2e_originalOnSuccessListener(src, event);
      };
      anyAwa._sde2e_originalOnFailureListener = anyAwa._sde2e_originalOnFailureListener || anyAwa.OnFailedVortexRequest;
      anyAwa.OnFailedVortexRequest = function (src: any, event: {} | {}[]) {
        anyAwa._sde2e_failedTelemetryTracker.push(event);
        anyAwa._sde2e_originalOnFailureListener(src, event);
      };
    }

    this.flightingService.whenReady().then(() => {
      this.flightingSvcReady = true;
      this.showInformativeAlert = this.flightingService.isFlightEnabled("CustomerTab_CST_EnableAPIErrorAlert");
    });
  }

  private logStateInfo(source: string) {
    this.loggingService.logInfo(
      `${source} sdController root state: ${this.currentRootState}, state: ${this.getCurrentState()}, login state: ${
        this.msalAuthenticationService.userInfo.isAuthenticated
      }`
    );
  }

  public isUsingStandardInformativeAlert() {
    if (!this.flightingSvcReady) {
      return false;
    }
    if (!this.flightingService.isFlightActiveSync("TopAlertFeature")) {
      return false;
    }
    let currentState: any = this.$state.$current;
    if (currentState.self.name) {
      return this.informativeAlertService.isUsingGlobalHeader(currentState);
    }
    return false;
  }
  public shouldShowLegacyInformativeAlert() {
    if (!this.flightingSvcReady) {
      return false;
    }
    if (!this.showInformativeAlert) {
      return false;
    }
    return (
      !this.isUsingStandardInformativeAlert() &&
      !this.informativeAlertService.statesWithInformativeAlertControl.find((i) => i === this.getCurrentState())
    );
  }

  public setRouteStates(toState: angular.ui.IState): void {
    let stateNames = "";
    if (toState && toState.name) {
      let allStates = toState.name.toLowerCase().split(".");
      let nestedStates = "";
      if (allStates && allStates.length > 0) {
        this.currentRootState = allStates[0];
        allStates.forEach((stateName) => {
          if (stateName) {
            // adding a prefix to avoid any collision
            stateName = "sdroute-" + stateName;
            nestedStates = nestedStates ? nestedStates + "-" + stateName : stateName;
            stateNames = stateNames + (stateNames != "" ? " " : "") + nestedStates;
          }
        });
      }
    }
    $("#uiContainer").attr("class", stateNames);
  }
  public getCurrentState(): string {
    return this.$state.current.name;
  }

  public setDisplayTheme(): string {
    switch (this.displayThemeService.getCurrentDisplayTheme()) {
      case darkTheme:
        return "sd-dark-theme";
      default:
        return "sd-light-theme";
    }
  }

  public login(): void {
    this.msalAuthenticationService.loginRedirect();
  }
  public logout(): void {
    this.msalAuthenticationService.logout();
  }

  public shouldShowBody(): boolean {
    let inReauthExperience = this.$window.location.href.indexOf("#/reauth") != -1;
    let inLogoutState = this.currentRootState == "logout";
    let isAuthenticated = this.msalAuthenticationService.userInfo.isAuthenticated;
    return (inReauthExperience || inLogoutState || isAuthenticated) && !this.loadingInProgress;
  }

  private getLoginFailureMessage(): UserVisibleMessage {
    return {
      title: this.sdLocalizationService.getLocalizedString("ServiceDesk", "_AUTH_ERROR_NOTIFICATION_TITLE_"),
      displayText: this.sdLocalizationService.getLocalizedString("ServiceDesk", "_AUTH_ERROR_NOTIFICATION_MESSAGE_"),
      alertActions: [
        {
          linkText: this.sdLocalizationService.getLocalizedString("ServiceDesk", "_AUTH_ERROR_REFRESH_ACTION_"),
          biId: "not-authorized-refresh",
          callback: () => this.$window.location.reload(true)
        },
        {
          linkText: this.sdLocalizationService.getLocalizedString("ServiceDesk", "_AUTH_ERROR_SIGNOUT_ACTION_"),
          biId: "not-authorized-sign-out",
          callback: () => this.logoutService.attemptLogout()
        }
      ]
    };
  }
}
